<script setup lang="ts">
import { getCurrentInstance, ref } from 'vue';

import BaseButton from '@/components/base/BaseButton.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import { useSubscribeForm } from '@/composables/useSubscribeForm';
import { getLabel } from '@/utils/data-display-utils';
import { Locales } from '@/store/language.interface';

// REFS
const emailInputRef = ref<InstanceType<typeof BaseInput> | null>(null);

// PROPS
const props = defineProps<{
  emailAlreadyInList: boolean;
  emailNeedsVerification: boolean;
  inputFilled?: boolean;
  userSubscribed: boolean;
  languageEnabled?: Locales[];
}>();

// EMITS
const emit = defineEmits<{
  (e: 'subscribe', values: { email: string }): void;
  (e: 'unsubscribe'): void;
}>();

const instance = getCurrentInstance();
const subscribeStatusId = `subscribe-status-${instance?.uid ?? 'base'}`;
const subscribeInputId = `subscribe-email-${instance?.uid ?? 'base'}`;
const subscribeLabelId = `subscribe-email-label-${instance?.uid ?? 'base'}`;
const subscribeErrorId = `subscribe-email-error-${instance?.uid ?? 'base'}`;

const { emailField, errorMessage, userLoggedIn, subscribe, unsubscribe, resetForm, updateEmailInput } =
  useSubscribeForm({
    onSubscribe: (values) => emit('subscribe', values as { email: string }),
    onUnsubscribe: () => emit('unsubscribe'),
    emailInputRef,
    validationMessages: {
      invalidEmail: getLabel('subscribe.validation.invalid-email', props.languageEnabled),
      requiredEmail: getLabel('subscribe.validation.required-email', props.languageEnabled),
    },
  });

defineExpose({
  resetForm,
});
</script>
<template>
  <div class="user-subscribe">
    <form class="buttons" @submit.prevent="subscribe" novalidate>
      <label v-if="!userLoggedIn" :id="subscribeLabelId" :for="subscribeInputId" class="sr-only">
        {{ getLabel('subscribe.email-placeholder', props.languageEnabled) }}
      </label>
      <BaseInput
        v-if="!userLoggedIn"
        :model="emailField"
        @update="updateEmailInput"
        :id="subscribeInputId"
        type="email"
        :placeholder="getLabel('subscribe.email-placeholder', props.languageEnabled)"
        :aria-labelledby="subscribeLabelId"
        :aria-describedby="errorMessage ? `${subscribeStatusId} ${subscribeErrorId}` : subscribeStatusId"
        :error="Boolean(errorMessage)"
        :error-message="errorMessage"
        autocomplete="email"
        name="email"
        ref="emailInputRef"
        class="email-input"
        :filled="props.inputFilled"
      />
      <BaseButton
        v-if="!props.userSubscribed"
        :text="getLabel('subscribe.button.subscribe', props.languageEnabled)"
        type="submit"
        primary
        class="subscribe-button"
      />
      <BaseButton
        v-if="props.userSubscribed && userLoggedIn"
        :text="getLabel('subscribe.button.unsubscribe', props.languageEnabled)"
        type="button"
        @click="unsubscribe"
        primary
        class="unsubscribe-button"
      />
    </form>
    <div :id="subscribeStatusId" class="status" aria-live="polite">
      <span v-if="errorMessage" :id="subscribeErrorId" class="sr-only">
        {{ errorMessage }}
      </span>
      <span v-if="props.emailAlreadyInList">
        {{ getLabel('subscribe.status.already-on-email-list', props.languageEnabled) }}
      </span>
      <span v-else-if="props.emailNeedsVerification">
        {{ getLabel('subscribe.status.need-email-verification', props.languageEnabled) }}
      </span>
    </div>
  </div>
</template>
<style lang="scss">
.user-subscribe {
  .buttons {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: $space-md;

    .q-field {
      width: 60%;
    }
  }
}
</style>
